<template>
  <m-dropdown
    v-model:value="expanded"
    class="ai-chat-skill-menu"
    :title="$t('aiChat.skills.title')"
    placement="bottomLeft"
    :get-trigger-dimensions="anchorDimensions"
    :relocate-key="rowCount"
  >
    <m-tooltip placement="top">
      <m-btn
        class="_open"
        icon="lucide-zap"
        hide-border
        fab
        small
        @click.stop="expanded = true"
      />
      <template #title>
        {{ $t('aiChat.skills.title') }}
      </template>
    </m-tooltip>
    <template #overlay>
      <m-card
        class="_menu"
        no-padding
        list
      >
        <template
          v-for="section in visibleSections"
          :key="section.key"
        >
          <m-card-item
            class="_section"
            disabled
          >
            {{ section.label }}
          </m-card-item>
          <m-card-item
            v-for="skill in section.skills"
            :key="`${section.key}-${skill.id}`"
            class="_skill"
            :icon="skill.icon"
            @click="use(skill)"
          >
            <span class="_title">{{ skill.title }}</span>
            <template
              v-if="!skill.builtin"
              #right
            >
              <div class="_skill-actions">
                <m-btn
                  class="_edit"
                  icon="edit"
                  light
                  hide-border
                  fab
                  xs
                  @click.stop="openForm(skill)"
                />
                <m-btn
                  class="_delete"
                  icon="delete"
                  light
                  hide-border
                  fab
                  xs
                  @click.stop="remove(skill)"
                />
              </div>
            </template>
          </m-card-item>
        </template>
        <m-divider xxs />
        <m-card-item
          class="_new"
          icon="plus"
          @click="openForm(null)"
        >
          {{ $t('aiChat.skills.new') }}
        </m-card-item>
      </m-card>
    </template>
  </m-dropdown>
  <ai-chat-skill-form
    v-if="formOpen"
    v-model:value="formOpen"
    :skill="editedSkill"
  />
</template>

<script setup>
import AiChatSkillForm from '@/components/ai-chat/AiChatSkillForm.vue';
import useAiChatSkills from '@/composables/ai-chat/ai-chat-skills';
import useConfirmDialog from '@/composables/confirm-dialog';
import useSnackBar from '@/composables/snackbar';
import { computed, ref } from 'vue';
import { logCatch } from '@/lib/logger/logger';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  anchor: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['use']);

const { t } = useI18n();
const snackbar = useSnackBar();
const confirmDialog = useConfirmDialog();
const { own, builtins, deleteSkill } = useAiChatSkills();

const expanded = ref(false);
const formOpen = ref(false);
const editedSkill = ref(null);

// the menu lines up with the composer box it belongs to rather than with its own small
// button, and flips above the box when there is no room below it
const anchorDimensions = (trigger) => (props.anchor ?? trigger)?.getBoundingClientRect() ?? null;

const visibleSections = computed(() => [
  { key: 'own', label: t('aiChat.skills.mine'), skills: own.value },
  { key: 'builtins', label: t('aiChat.skills.suggested'), skills: builtins.value },
].filter((section) => section.skills.length > 0));

// skills sync in from other tabs while the menu is open; a taller menu only stays clear of
// the composer if it measures itself again
const rowCount = computed(() => visibleSections.value.reduce((count, section) => count + 1 + section.skills.length, 0));

const onError = logCatch(() => { snackbar.error(); });

const use = (skill) => {
  expanded.value = false;
  emit('use', skill);
};

const openForm = (skill) => {
  expanded.value = false;
  editedSkill.value = skill;
  formOpen.value = true;
};

const remove = (skill) => {
  expanded.value = false;
  confirmDialog.confirm({
    title: t('aiChat.skills.deletePrompt'),
    onOk() {
      deleteSkill(skill).catch(onError);
    },
  });
};
</script>

<style
  scoped
  lang="scss"
  type="text/scss"
>
  ._menu {
    width: 32rem;
    max-height: 48rem;
    overflow-y: auto;

    ._section {
      font-size: $font-size-2;
      color: $font-color-secondary;
    }

    ._skill {
      ._title {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      ._skill-actions {
        display: flex;
        align-items: center;

        ._edit,
        ._delete {
          visibility: hidden;
        }
      }

      &:hover {
        ._skill-actions {
          ._edit,
          ._delete {
            visibility: visible;
          }
        }
      }
    }
  }
</style>
